import type { FieldErrors } from "@/lib/form/errors";
import type { TranslationStrings } from "@/lib/i18n/translations";

/** Matches API `MeUpdateRequest` maxLength for firstName / lastName. */
export const PROFILE_NAME_MAX = 100;

function tpl(
  template: string,
  vars: Record<string, string | number>,
): string {
  return template.replace(/\{(\w+)\}/g, (_, name: string) => String(vars[name] ?? ""));
}

/** Client-side checks before `PATCH /v1/user/me`. */
export function validateProfileNames(
  firstName: string,
  lastName: string,
  t: TranslationStrings,
): FieldErrors {
  const fields: FieldErrors = {};
  const first = firstName.trim();
  const last = lastName.trim();

  if (first.length > PROFILE_NAME_MAX) {
    fields.firstName = tpl(t.validationFieldMax, {
      field: t.accountFirstName,
      max: PROFILE_NAME_MAX,
    });
  }
  if (last.length > PROFILE_NAME_MAX) {
    fields.lastName = tpl(t.validationFieldMax, {
      field: t.accountLastName,
      max: PROFILE_NAME_MAX,
    });
  }

  return fields;
}

/** Empty / whitespace-only values become `null` (API clears the field). */
export function normalizeProfileName(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export type MissingProfileNameFields = {
  missingFirstName: boolean;
  missingLastName: boolean;
};

/** Which profile name fields are blank (certificate issuance needs both). */
export function getMissingProfileNameFields(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): MissingProfileNameFields | null {
  const missingFirstName = !firstName?.trim();
  const missingLastName = !lastName?.trim();
  if (!missingFirstName && !missingLastName) return null;
  return { missingFirstName, missingLastName };
}

export function isProfileNameComplete(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): boolean {
  return getMissingProfileNameFields(firstName, lastName) == null;
}

/** Localized warning when certificate download needs a complete profile name. */
export function profileNameWarningMessage(
  fields: MissingProfileNameFields,
  t: TranslationStrings,
): string {
  if (fields.missingFirstName && fields.missingLastName) {
    return t.certsProfileIncompleteBoth;
  }
  if (fields.missingFirstName) return t.certsProfileIncompleteFirst;
  return t.certsProfileIncompleteLast;
}
